import { Feature } from 'geojson';
import { inject, injectable, interfaces } from 'inversify';
import { cloneDeep } from 'lodash/fp';
import { action, makeObservable, observable } from 'mobx';
import { IEventBus } from '../../services/eventBus.service';
import { ISegmentationFeature, SegmentationGeometry } from './annotations.interface';
import { SegmentationDeletedEvent, SegmentationDeletedEventType } from './submodules/segmentations/events/sementationDeletedEvent';

export interface IFreeDrawSegmentationService {
  freeDrawFeature?: ISegmentationFeature;
  freeDrawSelected: boolean;
  freeDrawInProgress: boolean;
  showClearConfirmation: boolean;
  setFreeDrawFeature(segmentation: ISegmentationFeature): void;
  updateFreeDrawFeature(feature: Feature): void;
  startFreeDraw(): void;
  selectFeature(): void;
  clearAsync(): Promise<boolean> | undefined;
  clear(isDeleteAction?: boolean): void;
  cancelClear(): void;
}

export namespace IFreeDrawSegmentationService {
  export const $: interfaces.ServiceIdentifier<IFreeDrawSegmentationService> = Symbol('FREE_DRAW_SEGMENTATION_SERVICE');
}

@injectable()
export class FreeDrawSegmentationService implements IFreeDrawSegmentationService {
  constructor(@inject(IEventBus.$) private readonly eventBus: IEventBus) {
    makeObservable(this);
    this.eventBus.addListener<SegmentationDeletedEvent>(this.segmentationDeletedListener, SegmentationDeletedEventType);
  }

  @observable
  freeDrawSelected: boolean = false;

  @observable
  freeDrawInProgress: boolean = false;

  @observable
  showClearConfirmation: boolean = false;

  freeDrawFeature?: ISegmentationFeature;

  clearPromise?: Promise<boolean>;
  clearResolver?(value: boolean): void;

  setFreeDrawFeature(segmentation: ISegmentationFeature): void {
    this.freeDrawFeature = cloneDeep(segmentation);
  }

  updateFreeDrawFeature(feature: Feature<SegmentationGeometry>): void {
    if (this.freeDrawFeature) {
      this.freeDrawFeature.geometry = feature.geometry;
    }
  }

  @action.bound
  selectFeature(): void {
    this.freeDrawSelected = true;
  }

  @action.bound
  startFreeDraw(): void {
    this.freeDrawInProgress = true;
    this.freeDrawSelected = false;
  }

  clearAsync(): Promise<boolean> | undefined {
    if (!this.clearResolver) {
      this.clearPromise = new Promise((resolve, _) => {
        this.clearResolver = resolve.bind(this);
      });
      if (this.maybeClear()) {
        this.resolveClearPromise(true);
      }
    }
    return this.clearPromise;
  }

  @action.bound
  private maybeClear(): boolean {
    if (this.freeDrawInProgress && this.freeDrawFeature && this.freeDrawFeature.geometry.coordinates.length > 0) {
      this.showClearConfirmation = true;
      return false;
    }

    this.clear();
    return true;
  }

  @action.bound
  clear(isDeleteAction: boolean = false): void {
    if (this.freeDrawInProgress || this.freeDrawSelected || isDeleteAction) {
      this.showClearConfirmation = false;
      this.freeDrawFeature = undefined;
      this.freeDrawInProgress = false;
      this.freeDrawSelected = false;
      this.resolveClearPromise(true);
    }
  }

  @action.bound
  cancelClear(): void {
    this.showClearConfirmation = false;
    this.resolveClearPromise(false);
  }

  private resolveClearPromise(result: boolean): void {
    if (this.clearResolver) {
      this.clearResolver(result);
      this.clearResolver = undefined;
    }
  }

  private segmentationDeletedListener = (event: SegmentationDeletedEvent): void => {
    if (this.freeDrawFeature?.id === event.segmentationId) {
      this.clear(true);
    }
  };
}
